// Shared source of truth for public, indexable flat routes. The pages read their
// <title> and meta description from here too (src/lib/routeHead.ts), because the
// prerendered head and react-helmet's head both stay in the document.
// Dynamic categories, journal articles and products are added by their generators.
import { SHIPPING_META_DESCRIPTION } from "./site-policy.mjs";

export const STATIC_ROUTES = [
  {
    path: "/",
    // Must match the <OGTags> on src/pages/Index.tsx: both heads stay in the document.
    title: "Modern Loft Furniture — Sofas, Dining & Bedroom | LoftLoom",
    description: "Shop curated modern furniture for loft living: sofas, sectionals, dining, bedroom and lighting. Free U.S. shipping over $120 and 30-day returns.",
    type: "website",
    changefreq: "daily",
    priority: "1.0",
  },
  {
    path: "/best-sellers",
    // No claim about what LoftLoom's customers order: the page lists the supply
    // partner's best-selling order (src/pages/BestSellers.tsx reads this entry).
    title: "Best Sellers — Popular Furniture, Lighting & Decor | LoftLoom",
    description: "Furniture, lighting and decor from the LoftLoom catalogue, led by our supply partner's best sellers. Free U.S. shipping over $120 and 30-day returns.",
    type: "website",
    changefreq: "daily",
    priority: "0.8",
  },
  {
    path: "/about",
    title: "About LoftLoom — A Curated Furniture Storefront",
    description: "LoftLoom is a curated online storefront for modern furniture, lighting and decor. Orders are fulfilled by our supply partner across the U.S. and Canada.",
    type: "website",
    changefreq: "monthly",
    priority: "0.5",
  },
  {
    path: "/contact",
    title: "Contact LoftLoom — Questions & Order Help",
    description: "Contact LoftLoom by email with product questions, order enquiries or help choosing furniture for your space.",
    type: "website",
    changefreq: "monthly",
    priority: "0.5",
  },
  {
    path: "/faq",
    title: "LoftLoom FAQ — Shipping, Returns & Care",
    description: "Find answers about LoftLoom shipping, returns, payments, product care and order processing.",
    type: "website",
    changefreq: "monthly",
    priority: "0.5",
  },
  {
    path: "/shipping",
    title: "Shipping & Delivery — Free U.S. Shipping Over $120 | LoftLoom",
    description: SHIPPING_META_DESCRIPTION,
    type: "website",
    changefreq: "monthly",
    priority: "0.5",
  },
  {
    path: "/returns",
    title: "Returns — 30-Day Policy | LoftLoom",
    description: "Returns within 30 days of delivery for unused items in original packaging. We cover return shipping only if an item arrives damaged, defective or incorrect.",
    type: "website",
    changefreq: "monthly",
    priority: "0.5",
  },
  {
    path: "/privacy",
    title: "Privacy Policy | LoftLoom",
    description: "What LoftLoom collects, where it goes (including our fulfilment partner, analytics, advertising and AI services), and the choices you have.",
    type: "website",
    changefreq: "monthly",
    priority: "0.3",
  },
  {
    path: "/terms",
    title: "Terms of Use | LoftLoom",
    description: "The terms governing your use of the LoftLoom website, orders, pricing, and content.",
    type: "website",
    changefreq: "monthly",
    priority: "0.3",
  },
  {
    path: "/accessibility",
    title: "Accessibility Statement | LoftLoom",
    description: "Read LoftLoom's digital accessibility commitment and contact us if you encounter an access barrier.",
    type: "website",
    changefreq: "monthly",
    priority: "0.3",
  },
  {
    path: "/sustainability",
    title: "Sustainability | LoftLoom",
    description: "What LoftLoom can and can't tell you about materials and sourcing, and how to choose furniture that lasts.",
    type: "website",
    changefreq: "monthly",
    priority: "0.4",
  },
  {
    path: "/press",
    title: "Press & Media | LoftLoom",
    description: "Contact LoftLoom for press enquiries, product information and approved brand materials.",
    type: "website",
    changefreq: "monthly",
    priority: "0.4",
  },
  {
    path: "/design-services",
    title: "Free Room Plan — Design Help | LoftLoom",
    description: "Get a free room plan from LoftLoom: share your space, style and budget, and we'll suggest a furniture layout with pieces from our catalogue.",
    type: "website",
    changefreq: "monthly",
    priority: "0.6",
  },
  {
    path: "/trade-program",
    title: "Trade & Projects — Help Furnishing a Project | LoftLoom",
    description: "Furnishing several rooms or a client project? LoftLoom can shortlist pieces from the catalogue and check availability before you order. No trade discounts.",
    type: "website",
    changefreq: "monthly",
    priority: "0.5",
  },
  {
    path: "/design-your-room",
    // Linked as "Room Bundles" in the main nav; it had no prerendered file, so
    // crawlers got the homepage's title and canonical.
    title: "Design Your Room — Bundle Builder | LoftLoom",
    description: "Pick a style and a room, and get a starting set of in-stock pieces for that room — swap or remove any of them before adding to cart.",
    type: "website",
    changefreq: "weekly",
    priority: "0.6",
  },
  {
    path: "/financing",
    title: "Payment Options — How You Pay at Checkout | LoftLoom",
    description: "LoftLoom doesn't offer financing of its own. Payment is taken on the checkout page, using the options shown there.",
    type: "website",
    changefreq: "monthly",
    priority: "0.3",
  },
  {
    path: "/ideas",
    title: "LoftLoom Journal — Design Guides, Comparisons & Styling Ideas",
    description: "Sizing guides, honest comparisons, and styling ideas from LoftLoom — from sofa selection to small-space layouts.",
    type: "website",
    changefreq: "weekly",
    priority: "0.7",
  },
  {
    path: "/categories",
    title: "Furniture Categories — Shop by Room | LoftLoom",
    // Must match the <OGTags> on src/pages/Categories.tsx.
    description: "Browse modern furniture by room: living, bedroom, dining, office, outdoor, lighting, decor and rugs. Free U.S. shipping over $120 and 30-day returns.",
    type: "website",
    changefreq: "weekly",
    priority: "0.8",
  },
  {
    path: "/get-the-look",
    title: "Get the Look — Shoppable Room Inspiration | LoftLoom",
    description: "Explore editorial room scenes and shop each look by category: warm minimalist living rooms, Japandi spaces, walnut dining rooms and quiet bedrooms.",
    type: "website",
    changefreq: "weekly",
    priority: "0.7",
  },
  {
    path: "/style-quiz",
    title: "Style Quiz — Build Your Room Board in a Minute | LoftLoom",
    description: "Answer five quick questions about room, palette, materials, mood and budget, and we'll assemble a shoppable room board from pieces currently in stock.",
    type: "website",
    // The share image src/pages/StyleQuiz.tsx passes to OGTags; the served page had the sitewide one.
    image: "/journal/28-hero.webp",
    changefreq: "monthly",
    priority: "0.7",
  },
  {
    path: "/tools/rug-size-calculator",
    title: "Rug Size Calculator — Living Room, Bedroom & Dining | LoftLoom",
    description: "Free rug size calculator: enter your room, bed or dining table and get the standard rug size that fits, with the placement rules behind it.",
    type: "website",
    changefreq: "monthly",
    priority: "0.7",
  },
  {
    path: "/tools/chandelier-size-calculator",
    title: "Chandelier Size Calculator — Diameter & Hanging Height | LoftLoom",
    description: "Free chandelier size calculator: get the fixture diameter for your room or dining table and how high to hang it for your ceiling height.",
    type: "website",
    changefreq: "monthly",
    priority: "0.7",
  },
  {
    path: "/tools/dining-table-size-calculator",
    title: "Dining Table Size Calculator — Seats & Clearance | LoftLoom",
    description: "Free dining table size calculator: enter your room and how many you seat, and see the table that fits with the clearance left on every side.",
    type: "website",
    changefreq: "monthly",
    priority: "0.7",
  },
  {
    path: "/tools/wall-art-size-calculator",
    title: "Wall Art Size Calculator — Width & Hanging Height | LoftLoom",
    description: "Free wall art size calculator: how wide art should be over a sofa, bed or console, and how high to hang it on any wall.",
    type: "website",
    changefreq: "monthly",
    priority: "0.7",
  },
  {
    path: "/tools/lampshade-size-calculator",
    title: "Lampshade Size Calculator — Match Shade to Lamp | LoftLoom",
    description: "Free lampshade size calculator: measure your lamp base and get the shade width and height that suit it.",
    type: "website",
    changefreq: "monthly",
    priority: "0.7",
  },
  {
    path: "/tools/coffee-table-size-calculator",
    title: "Coffee Table Size Calculator — Length, Height & Gap | LoftLoom",
    description: "Free coffee table size calculator: enter your sofa's length and seat height and get the table length, height and spacing that suit it.",
    type: "website",
    changefreq: "monthly",
    priority: "0.7",
  },
  {
    path: "/tools/bar-stool-height-calculator",
    title: "Bar Stool Height Calculator — Seat Height & Spacing | LoftLoom",
    description: "Free bar stool height calculator: enter your counter or bar height and get the seat height to buy and how many stools fit along it.",
    type: "website",
    changefreq: "monthly",
    priority: "0.7",
  },
  {
    path: "/tools/sofa-door-fit-calculator",
    title: "Will My Sofa Fit Through the Door? Fit Calculator | LoftLoom",
    description: "Free sofa door fit calculator: enter your doorway width and the sofa's depth, height and diagonal depth to see if it fits, flat or angled.",
    type: "website",
    changefreq: "monthly",
    priority: "0.7",
  },
  {
    path: "/tools/christmas-tree-size-calculator",
    title: "Christmas Tree Size Calculator — Height for Your Ceiling | LoftLoom",
    description: "Free Christmas tree size calculator: enter your ceiling height and get the tallest tree that fits, how wide it will be, and how many lights and ornaments to plan.",
    type: "website",
    changefreq: "monthly",
    priority: "0.7",
  },
  {
    path: "/tools",
    title: "Free Home Sizing Calculators — Rugs, Lighting & More | LoftLoom",
    description: "Free calculators for rug, chandelier, dining table, wall art, lampshade and coffee table size, bar stool height, sofa door fit and Christmas tree size, built on our sizing guides.",
    type: "website",
    changefreq: "monthly",
    priority: "0.6",
  },
  {
    path: "/order-tracking",
    title: "Order Help | LoftLoom",
    description: "Find your FurniFindUSA order confirmation and tracking link, or contact LoftLoom for help following up on an order.",
    type: "website",
    changefreq: "monthly",
    priority: "0.4",
  },
];